import { JobAssignee } from '@eigtech/assignments-types'
import { ClaimV2Corn } from '@eigtech/claims-v2-types'
import { Note, NoteDataSource } from '@eigtech/notes-types'
import { ensureJobCorn } from '@eigtech/restoration-util'
import { Corn, getCornKernels } from '@eigtech/shared-corn'
import { isClaimCorn } from '@eigtech/shared-corn-helper'
import { ContextualCan } from '@eigtech/ui-shared-auth'
import {
  useCanPublishClaimInfoToXA,
  useGetAssignmentsForClaim,
} from '@eigtech/ui-shared-claims-lib'
import { useDatesContext } from '@eigtech/ui-shared-dates'
import {
  Badge,
  CircularProgress,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  Tooltip,
  XaGlobeIcon,
  chakra,
  useDisclosure,
} from '@eigtech/ui-shared-dave'
import { getJobClaim, useGetAssignablesForJob } from '@eigtech/ui-shared-jobs'
import { useGetNoteImportStatus } from '@eigtech/ui-shared-notes'
import { FiCheckCircle as ImportedIcon, FiCircle as PendingIcon } from 'react-icons/fi'
import { MdError } from 'react-icons/md'
import { TiExport as ExportIcon } from 'react-icons/ti'
import { ExportNoteDrawer } from './ExportNoteDrawer/ExportNoteDrawer'

export type NoteImportStatusProps = {
  entityCorn: Corn
  jobClaimCorn?: ClaimV2Corn
  note: Note
}

export function NoteImportStatus({ entityCorn, note }: NoteImportStatusProps) {
  const { data: assignables } = useGetAssignablesForJob(entityCorn)
  const { resourceType } = getCornKernels(entityCorn)

  let jobClaimCorn: ClaimV2Corn | undefined = undefined
  if (resourceType === 'job') {
    jobClaimCorn = getJobClaim(assignables)?.claimNumber
  }
  const claimCorn = jobClaimCorn || entityCorn

  return (
    <HStack spacing="1">
      {isClaimCorn(claimCorn) && <Status claimCorn={claimCorn} note={note} />}
      <ContextualCan I="update" a="note">
        <Export {...{ entityCorn, note, jobClaimCorn }} />
      </ContextualCan>
    </HStack>
  )
}

function Export(props: NoteImportStatusProps) {
  const { resourceType } = getCornKernels(props.entityCorn)

  switch (resourceType) {
    case 'claim':
      return <NoteClaimExport {...props} />
    case 'job': {
      // doing more than necessary to appease TS
      const { jobClaimCorn } = props
      if (!jobClaimCorn) {
        return null
      }
      return <NoteJobExport {...props} jobClaimCorn={jobClaimCorn} />
    }

    default:
      return <></>
  }
}

function NoteClaimExport({ entityCorn, note }: Pick<NoteImportStatusProps, 'entityCorn' | 'note'>) {
  const { data: assignments } = useGetAssignmentsForClaim(entityCorn)
  const { data: noteImportStatus } = useGetNoteImportStatus(entityCorn)

  const { latestImportStatus } = noteImportStatus?.[note.id] ?? {}

  const jobPublicationTargets = assignments
    ?.filter((assignment) => assignment.assignee.type === 'job')
    .map((assignment) => {
      const { job } = assignment.assignee as JobAssignee
      return {
        value: ensureJobCorn(job.jobId),
        label: (
          <HStack>
            <Badge>Job</Badge>
            <Stack lineHeight={1.2} spacing="0">
              <Text as="span" fontWeight="bold">
                {job.jobId.replace('job_', '')}
              </Text>
              <Text as="span" whiteSpace="pre-line">
                {job.jobDescription}
              </Text>
            </Stack>
          </HStack>
        ),
      }
    })

  const exportNoteDrawer = useDisclosure()

  const allTargets = [
    ...(note.dataSource === NoteDataSource.Verisk ? [] : ['XactAnalysis']),
    ...(jobPublicationTargets ?? []).map(({ value }) => value),
  ]
  const hasExportedToAllTargets =
    latestImportStatus !== 'failed' &&
    allTargets.every((target) => note.publicationTargets?.includes(target))

  return (
    <>
      <Tooltip
        label={
          hasExportedToAllTargets
            ? 'This note has been exported to all available targets'
            : note.isPublishable === false
              ? 'This note cannot be exported'
              : 'Export Note'
        }
      >
        <IconButton
          aria-label="Export Note"
          icon={<Icon as={ExportIcon} boxSize="6" />}
          isDisabled={hasExportedToAllTargets || note.isPublishable === false}
          isRound={false}
          size="sm"
          variant="outline"
          onClick={exportNoteDrawer.onOpen}
        />
      </Tooltip>

      {exportNoteDrawer.isOpen && (
        <ExportNoteDrawer
          {...exportNoteDrawer}
          additionalPublicationTargets={jobPublicationTargets}
          entityCorn={entityCorn}
          excludeXaPublicationTarget={note.dataSource === NoteDataSource.Verisk}
          latestImportStatus={latestImportStatus}
          note={note}
        />
      )}
    </>
  )
}

function NoteJobExport({
  entityCorn,
  jobClaimCorn,
  note,
}: Required<Pick<NoteImportStatusProps, 'entityCorn' | 'note' | 'jobClaimCorn'>>) {
  const { data: noteImportStatus } = useGetNoteImportStatus(jobClaimCorn)

  const { latestImportStatus } = noteImportStatus?.[note.id] ?? {}

  const exportNoteDrawer = useDisclosure()

  const jobClaimNumber = getCornKernels(jobClaimCorn).resourceId

  const includeXaPublishField = useCanPublishClaimInfoToXA({
    claimNumber: jobClaimNumber,
  })

  const allTargets = includeXaPublishField ? ['XA', jobClaimCorn] : [jobClaimCorn]

  const hasExportedToAllTargets =
    latestImportStatus !== 'failed' &&
    allTargets.every((target) => note.publicationTargets?.includes(target))

  if (!jobClaimCorn) {
    return null
  }

  return (
    <>
      <Tooltip
        label={
          hasExportedToAllTargets
            ? 'This note has been exported to all available targets'
            : 'Export Note'
        }
      >
        <IconButton
          aria-label="Export Note"
          icon={<Icon as={ExportIcon} boxSize="6" />}
          isDisabled={hasExportedToAllTargets}
          isRound={false}
          size="sm"
          variant="outline"
          onClick={exportNoteDrawer.onOpen}
        />
      </Tooltip>

      {exportNoteDrawer.isOpen && (
        <ExportNoteDrawer
          {...exportNoteDrawer}
          additionalPublicationTargets={[
            { label: `Claim: ${jobClaimNumber}`, value: jobClaimCorn },
          ]}
          entityCorn={entityCorn}
          excludeXaPublicationTarget={!includeXaPublishField}
          latestImportStatus={latestImportStatus}
          note={note}
        />
      )}
    </>
  )
}

function Status({ claimCorn, note }: { claimCorn: Corn; note: Note }) {
  const { data: noteImportStatus, isPending, isError } = useGetNoteImportStatus(claimCorn)

  const importStatus = noteImportStatus?.[note.id]

  const { PreferredDateTimeComponent } = useDatesContext()

  if (isPending) {
    return <CircularProgress isIndeterminate size="8" />
  }

  if (isError) {
    return (
      <Tooltip label="Could not load note XA import status.">
        <Icon as={MdError} boxSize="8" color="red.500" />
      </Tooltip>
    )
  }

  if (importStatus?.latestImportStatus || note.publicationTargets?.includes('XactAnalysis')) {
    const formattedDate = (
      <PreferredDateTimeComponent property="latestStatusDate">
        {importStatus?.latestStatusDate}
      </PreferredDateTimeComponent>
    )

    const { label, statusIcon } =
      importStatus?.latestImportStatus === 'confirmed'
        ? {
            label: <>Confirmed imported to XA {formattedDate}</>,
            statusIcon: <Icon as={XaGlobeIcon} boxSize="6" />,
          }
        : importStatus?.latestImportStatus === 'sent'
          ? {
              label: <>Imported to XA {formattedDate}</>,
              statusIcon: <Icon as={ImportedIcon} boxSize="6" />,
            }
          : importStatus?.latestImportStatus === 'failed'
            ? {
                label: <>Failed to Import to XA {formattedDate}</>,
                statusIcon: <Icon as={MdError} boxSize="6" color="red.500" />,
              }
            : { label: 'Importing to XA', statusIcon: <Icon as={PendingIcon} boxSize="6" /> }

    return (
      <Tooltip label={label} placement="top">
        <chakra.span>{statusIcon}</chakra.span>
      </Tooltip>
    )
  }

  return null
}
